import {
  Bell,
  CreditCard,
  Home,
  Package,
  User,
  UserCheck,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface NavItemDefinition {
  title: string;
  url: string;
  icon: LucideIcon;
  matchesNestedRoutes?: boolean;
}

interface NavSectionDefinition {
  label: string;
  items: NavItemDefinition[];
}

interface ResolvedNavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  isActive: boolean;
}

interface ResolvedNavSection {
  label: string;
  items: ResolvedNavItem[];
}

interface BreadcrumbConfig {
  title: string;
  parent?: string;
}

export interface BreadcrumbEntry {
  title: string;
  path: string;
}

const navigationSectionsDefinition: NavSectionDefinition[] = [
  {
    label: "Prehľad",
    items: [
      {
        title: "Dashboard",
        url: "/user/dashboard",
        icon: Home,
      },
    ],
  },
  {
    label: "Lekcie a kurzy",
    items: [
      {
        title: "Súkromné hodiny",
        url: "/user/private-lessons",
        icon: User,
      },
      {
        title: "Skupinové kurzy",
        url: "/user/courses",
        icon: Users,
      },
      {
        title: "Moje skupiny",
        url: "/user/dance-groups",
        icon: Users,
        matchesNestedRoutes: true,
      },
    ],
  },
  {
    label: "Balíky a platby",
    items: [
      {
        title: "Balíky",
        url: "/user/packages",
        icon: Package,
      },
      {
        title: "Moje transakcie",
        url: "/user/transactions",
        icon: CreditCard,
      },
    ],
  },
  {
    label: "Spolupráca",
    items: [
      {
        title: "Párová správa",
        url: "/user/pairs",
        icon: UserCheck,
      },
    ],
  },
  {
    label: "Môj účet",
    items: [
      {
        title: "Notifikácie",
        url: "/user/notifications",
        icon: Bell,
      },
      {
        title: "Môj profil",
        url: "/user/account",
        icon: User,
      },
    ],
  },
];

const breadcrumbConfig: Record<string, BreadcrumbConfig> = {
  "/": { title: "Domov" },
  "/user/dashboard": { title: "Dashboard", parent: "/" },
  "/user/courses": { title: "Skupinové kurzy", parent: "/user/dashboard" },
  "/user/dance-groups": { title: "Moje skupiny", parent: "/user/dashboard" },
  "/user/packages": { title: "Balíky", parent: "/user/dashboard" },
  "/user/private-lessons": {
    title: "Súkromné hodiny",
    parent: "/user/dashboard",
  },
  "/user/pairs": {
    title: "Párová správa",
    parent: "/user/dashboard",
  },
  "/user/account": { title: "Môj účet", parent: "/user/dashboard" },
  "/user/notifications": { title: "Notifikácie", parent: "/user/dashboard" },
  "/user/transactions": { title: "Moje transakcie", parent: "/user/dashboard" },
};

const resolveIsActive = (
  url: string,
  pathname: string,
  matchesNestedRoutes: boolean,
): boolean => {
  if (url.startsWith("#")) {
    return false;
  }

  if (url === "/") {
    return pathname === "/";
  }

  if (pathname === url) {
    return true;
  }

  if (!matchesNestedRoutes) {
    return false;
  }

  return pathname.startsWith(`${url}/`);
};

export const getNavigationSections = (
  pathname: string,
): ResolvedNavSection[] => {
  return navigationSectionsDefinition
    .map((section) => {
      const items = section.items.map((item) => ({
        title: item.title,
        url: item.url,
        icon: item.icon,
        isActive: resolveIsActive(
          item.url,
          pathname,
          item.matchesNestedRoutes ?? false,
        ),
      }));

      return {
        label: section.label,
        items,
      };
    })
    .filter((section) => section.items.length > 0);
};

const formatSegment = (segment: string): string => {
  return segment
    .split("-")
    .map((part) =>
      part.length > 0 ? part[0].toUpperCase() + part.slice(1) : part,
    )
    .join(" ");
};

export const getBreadcrumbItems = (pathname: string): BreadcrumbEntry[] => {
  let normalizedPath = pathname;

  if (normalizedPath.length > 1 && normalizedPath.endsWith("/")) {
    normalizedPath = normalizedPath.replace(/\/+$/, "");
  }

  const trail: BreadcrumbEntry[] = [
    { path: "/", title: breadcrumbConfig["/"].title },
  ];

  if (normalizedPath === "/") {
    return trail;
  }

  const segments = normalizedPath.split("/").filter(Boolean);
  let currentPath = "";

  segments.forEach((segment) => {
    currentPath = `${currentPath}/${segment}`;
    const config = breadcrumbConfig[currentPath];
    trail.push({
      path: currentPath,
      title: config?.title ?? formatSegment(segment),
    });
  });

  return trail;
};

export type {
  NavSectionDefinition,
  NavItemDefinition,
  ResolvedNavItem,
  ResolvedNavSection,
};
